/**
 * La Armería — Configuración declarativa de cada herramienta.
 *
 * Cada entrada define:
 *  - id: matches backend tool_id
 *  - title, tagline, emoji, category
 *  - fields: dynamic form schema { name, label, type, placeholder, options?, help? }
 *      types: "text", "textarea", "select", "options", "range"
 *  - resultRenderer: name of a component in ArmeriaResults.jsx that renders the result
 *  - transformInputs (optional): fn(inputs) → new inputs (for computed fields)
 */

export const ARMERIA_TOOLS_CONFIG = {
  "test-voz": {
    id: "test-voz",
    title: "Test de Voz Editorial",
    tagline: "Descubre tu arquetipo editorial analizando tu escritura real",
    emoji: "🎙️",
    category: "voz",
    ctaLabel: "Analizar mi voz",
    fields: [
      {
        name: "user_text",
        label: "Tu escritura real",
        type: "textarea",
        placeholder: "Escribe o pega aquí tu texto...\n\nEjemplos válidos: artículo de blog, post de LinkedIn, email a clientes, presentación...\n\nMínimo 200 palabras para un análisis preciso.",
        minWords: 40,
      },
      {
        name: "q1",
        label: "1/3 · ¿Cuál es tu audiencia principal?",
        type: "options",
        options: [
          { value: "profesionales", label: "Profesionales y empresas — espera rigor, datos y autoridad" },
          { value: "emprendedores", label: "Emprendedores y autónomos — quiere soluciones prácticas y cercanas" },
          { value: "comunidad", label: "Una comunidad o nicho específico — valora la autenticidad y la pertenencia" },
          { value: "general", label: "Público general — quiere claridad y accesibilidad ante todo" },
        ],
      },
      {
        name: "q2",
        label: "2/3 · ¿Qué efecto quieres provocar en el lector?",
        type: "options",
        options: [
          { value: "informado", label: "Informado y con contexto — que ha aprendido algo concreto y aplicable" },
          { value: "motivado", label: "Motivado y con energía — que quiere actuar ahora mismo" },
          { value: "reflexivo", label: "Reflexivo y cuestionado — que ve algo de otra manera" },
          { value: "conectado", label: "Conectado y comprendido — que siente que le hablas exactamente a él" },
        ],
      },
      {
        name: "q3",
        label: "3/3 · ¿Cómo te posicionas al escribir?",
        type: "options",
        options: [
          { value: "experto", label: "Como experto que enseña desde la autoridad y el conocimiento demostrado" },
          { value: "igual", label: "Como igual que comparte experiencias, dudas y aprendizajes propios" },
          { value: "guia", label: "Como guía que acompaña al lector en su proceso sin imponerse" },
          { value: "provocador", label: "Como provocador que cuestiona lo establecido y genera debate" },
        ],
      },
    ],
    resultRenderer: "TestVozResult",
  },

  "perfil-editorial": {
    id: "perfil-editorial",
    title: "Analizador de Perfil Editorial",
    tagline: "Descubre qué tipo de contenido necesita tu negocio",
    emoji: "🧭",
    category: "estrategia",
    ctaLabel: "Analizar mi negocio",
    fields: [
      { name: "q1", label: "1/4 · Describe brevemente a qué te dedicas", type: "textarea",
        placeholder: "Ej: Soy fisioterapeuta con clínica propia en Valencia, especializado en deportistas. Tengo 3 empleados y llevo 8 años en el sector..." },
      { name: "q2", label: "2/4 · ¿Quién es tu cliente ideal y qué problema le resuelves?", type: "textarea",
        placeholder: "Ej: Deportistas amateur de 30-50 años con lesiones recurrentes que buscan volver a su actividad sin recaídas..." },
      { name: "q3", label: "3/4 · ¿Cómo consigues clientes ahora mismo?", type: "options",
        options: [
          { value: "recomendacion", label: "Por recomendación y boca a boca" },
          { value: "rrss", label: "A través de redes sociales" },
          { value: "ads", label: "Con publicidad de pago (Google/Meta Ads)" },
          { value: "sin-flujo", label: "No tengo un flujo claro — es irregular" },
        ]},
      { name: "q4", label: "4/4 · ¿Qué quieres conseguir con tu contenido?", type: "options",
        options: [
          { value: "contacto", label: "Que me contacten directamente — quiero clientes" },
          { value: "autoridad", label: "Ser reconocido como referente en mi sector" },
          { value: "audiencia", label: "Construir audiencia — que más personas me conozcan" },
          { value: "fidelizacion", label: "Fidelizar a mis clientes actuales" },
        ]},
    ],
    resultRenderer: "PerfilEditorialResult",
  },

  "estilo-sector": {
    id: "estilo-sector",
    title: "Recomendador de Estilo por Sector",
    tagline: "Configuración editorial óptima para tu sector",
    emoji: "🎯",
    category: "estrategia",
    ctaLabel: "Recomendar estilo",
    fields: [
      { name: "sector", label: "¿En qué sector trabajas?", type: "options",
        options: [
          { value: "salud", label: "Salud y bienestar" },
          { value: "tech", label: "Tecnología y software" },
          { value: "consultoria", label: "Consultoría y coaching" },
          { value: "legal", label: "Legal y fiscalidad" },
          { value: "educacion", label: "Educación y formación" },
          { value: "ecommerce", label: "E-commerce y retail" },
          { value: "inmobiliario", label: "Inmobiliario" },
          { value: "creativo", label: "Creativo (diseño/arte/media)" },
          { value: "otro", label: "Otro sector" },
        ]},
      { name: "negocio_desc", label: "Describe brevemente tu negocio (opcional pero mejora el análisis)", type: "text", optional: true,
        placeholder: "Ej: Fisioterapia deportiva en Valencia, foco en amateur adults..." },
      { name: "audiencia", label: "¿A quién va dirigido tu contenido?", type: "options",
        options: [
          { value: "b2b", label: "Profesionales B2B" },
          { value: "b2c-premium", label: "Clientes finales de alto poder adquisitivo" },
          { value: "b2c-general", label: "Clientes finales público general" },
          { value: "emprendedores", label: "Otros emprendedores/autónomos" },
        ]},
      { name: "objetivo", label: "¿Cuál es tu objetivo principal?", type: "options",
        options: [
          { value: "clientes", label: "Conseguir clientes directos" },
          { value: "autoridad", label: "Construir autoridad y referencia" },
          { value: "seo", label: "Ganar tráfico orgánico (SEO)" },
          { value: "comunidad", label: "Crear comunidad" },
        ]},
    ],
    resultRenderer: "EstiloSectorResult",
  },

  "titulares-seo": {
    id: "titulares-seo",
    title: "Generador de Titulares SEO",
    tagline: "8 titulares optimizados con ganchos de apertura",
    emoji: "🎯",
    category: "seo",
    ctaLabel: "Generar 8 titulares",
    fields: [
      { name: "tema", label: "Tema del artículo", type: "text",
        placeholder: "Ej: productividad para autónomos, inteligencia artificial en marketing..." },
      { name: "intencion", label: "Intención de búsqueda", type: "select",
        options: [
          { value: "informacional", label: "Informacional (cómo, qué, guía)" },
          { value: "transaccional", label: "Transaccional (comprar, contratar)" },
          { value: "comercial", label: "Comercial (comparativa, mejor)" },
        ]},
      { name: "tipo", label: "Tipo de artículo", type: "select",
        options: [
          { value: "guia", label: "Guía definitiva" },
          { value: "lista", label: "Lista numerada" },
          { value: "como", label: "Cómo hacer" },
          { value: "opinion", label: "Opinión / editorial" },
          { value: "errores", label: "Errores a evitar" },
          { value: "comparativa", label: "Comparativa" },
        ]},
      { name: "contexto", label: "Contexto adicional (opcional)", type: "text", optional: true,
        placeholder: "Ej: para consultores de RRHH en España" },
    ],
    resultRenderer: "TitularesSeoResult",
  },

  "visibilidad-google": {
    id: "visibilidad-google",
    title: "Calculadora de Visibilidad Google",
    tagline: "Descubre cuándo empezarás a posicionar",
    emoji: "📊",
    category: "seo",
    ctaLabel: "Calcular visibilidad",
    fields: [
      { name: "articulos", label: "Artículos publicados", type: "range", min: 0, max: 200, step: 1, defaultValue: 10 },
      { name: "palabras", label: "Extensión media (palabras)", type: "range", min: 200, max: 3000, step: 100, defaultValue: 800 },
      { name: "frecuencia", label: "Frecuencia (artículos/mes)", type: "range", min: 0, max: 20, step: 1, defaultValue: 2 },
      { name: "competencia", label: "Nivel de competencia en tu sector", type: "options",
        options: [
          { value: "baja", label: "Baja" }, { value: "media", label: "Media" }, { value: "alta", label: "Alta" }
        ]},
      { name: "web", label: "¿Tienes web propia con blog?", type: "options",
        options: [
          { value: "si", label: "Sí, con blog activo" },
          { value: "no", label: "No tengo web" },
          { value: "solo-redes", label: "Solo redes sociales" }
        ]},
      { name: "negocio", label: "Cuéntame sobre tu negocio (mejora el análisis)", type: "text", optional: true,
        placeholder: "Ej: consultor de RRHH para empresas medianas..." },
    ],
    resultRenderer: "VisibilidadResult",
    transformInputs: (inputs) => {
      // Compute derived values that the prompt uses
      const art = parseInt(inputs.articulos) || 0;
      const pal = parseInt(inputs.palabras) || 800;
      const frec = parseInt(inputs.frecuencia) || 0;
      const compFactor = { baja: 1.2, media: 1.0, alta: 0.7 }[inputs.competencia] || 1;
      const artEquiv = Math.round(art * (pal / 800) * compFactor);
      const umbralMin = inputs.competencia === "alta" ? 100 : (inputs.competencia === "media" ? 50 : 25);
      const umbralOpt = umbralMin * 2;
      const mesesMin = frec > 0 ? Math.ceil((umbralMin - artEquiv) / frec) : 999;
      const mesesForja = Math.max(1, Math.ceil((umbralMin - artEquiv) / 4));
      return {
        ...inputs,
        art_equiv: artEquiv,
        umbral_min: umbralMin,
        umbral_opt: umbralOpt,
        meses_min: mesesMin > 100 ? "nunca a este ritmo" : `${mesesMin} meses`,
        meses_forja: mesesForja,
      };
    },
  },

  "angulos-editoriales": {
    id: "angulos-editoriales",
    title: "Generador de Ángulos Editoriales",
    tagline: "8 formas distintas de contar la misma historia",
    emoji: "📐",
    category: "creatividad",
    ctaLabel: "Generar 8 ángulos",
    fields: [
      { name: "tema", label: "Tu tema", type: "text",
        placeholder: "Ej: productividad para autónomos, inteligencia artificial en marketing..." },
      { name: "formato", label: "Formato de contenido", type: "select",
        options: [
          { value: "Artículo de blog", label: "Artículo de blog" },
          { value: "Post de LinkedIn", label: "Post de LinkedIn" },
          { value: "Newsletter", label: "Newsletter" },
          { value: "Guión de vídeo", label: "Guión de vídeo" },
          { value: "Cualquier formato", label: "Cualquier formato" },
        ]},
      { name: "audiencia", label: "Audiencia", type: "select",
        options: [
          { value: "Emprendedores", label: "Emprendedores" },
          { value: "Profesionales B2B", label: "Profesionales B2B" },
          { value: "Autónomos", label: "Autónomos" },
          { value: "Empresas y equipos", label: "Empresas y equipos" },
          { value: "Público general", label: "Público general" },
        ]},
    ],
    resultRenderer: "AngulosResult",
  },

  "diagnostico-contenido": {
    id: "diagnostico-contenido",
    title: "Diagnóstico de Contenido",
    tagline: "Análisis honesto de tu contenido real",
    emoji: "🔍",
    category: "voz",
    ctaLabel: "Diagnosticar",
    fields: [
      { name: "contenido", label: "Pega aquí tu contenido", type: "textarea", minWords: 20,
        placeholder: "Pega aquí tu artículo, post de LinkedIn, newsletter, email de ventas...\n\nMínimo 100 palabras para un diagnóstico preciso." },
      { name: "formato", label: "¿Qué tipo de contenido es?", type: "options",
        options: [
          { value: "Artículo", label: "Artículo" },
          { value: "LinkedIn", label: "LinkedIn" },
          { value: "Email", label: "Email" },
          { value: "Newsletter", label: "Newsletter" },
        ]},
    ],
    resultRenderer: "DiagnosticoResult",
  },
};

export const getToolConfig = (id) => ARMERIA_TOOLS_CONFIG[id] || null;
export const listTools = () => Object.values(ARMERIA_TOOLS_CONFIG);
